import Vue from 'vue';
import VueI18n from 'vue-i18n';
import { langs } from '/lang';
import countries from 'i18n-iso-countries';
import csCountries from 'i18n-iso-countries/langs/cs.json';
import skCountries from 'i18n-iso-countries/langs/sk.json';
import enCountries from 'i18n-iso-countries/langs/en.json';
import ukCountries from 'i18n-iso-countries/langs/uk.json';
import ruCountries from 'i18n-iso-countries/langs/ru.json';
import huCountries from 'i18n-iso-countries/langs/hu.json';

countries.registerLocale(csCountries);
countries.registerLocale(skCountries);
countries.registerLocale(enCountries);
countries.registerLocale(ukCountries);
countries.registerLocale(ruCountries);
countries.registerLocale(huCountries);

const buildCountryMap = (locale = 'en', priority = ['CZ', 'SK', 'HU']) => {
  // If this locale isn't registered yet, fall back to 'en'
  const source = countries.getNames(locale) || countries.getNames('en') || {};

  const out = {};
  const seen = new Set();

  // 1) add priority countries in given order (if present in the locale)
  for (const code of priority) {
    const name = source[code];
    if (name) {
      out[code] = name;
      seen.add(code);
    }
  }

  // 2) add the rest, sorted by localized name
  const rest = Object.entries(source)
    .filter(([code]) => !seen.has(code))
    .sort((left, right) => left[1].localeCompare(right[1], locale));

  for (const [code, name] of rest) {
    out[code] = name;
  }

  return out;
};

const buildCzRegionCountryMap = locale => {
  return buildCountryMap(locale, ['CZ', 'SK', 'HU']);
};

const buildSkRegionCountryMap = locale => {
  return buildCountryMap(locale, ['SK', 'HU', 'CZ']);
};

const buildHuRegionCountryMap = locale => {
  return buildCountryMap(locale, ['HU', 'SK', 'CZ']);
};

Vue.use(VueI18n);

const formattingBase = {
  dateTimeFormats: {
    cs: {
      short: { day: '2-digit', month: '2-digit', year: 'numeric' }
    },
    sk: {
      short: { day: '2-digit', month: '2-digit', year: 'numeric' }
    },
    en: {
      short: { month: '2-digit', day: '2-digit', year: 'numeric' }
    },
    uk: {
      short: { month: '2-digit', day: '2-digit', year: 'numeric' }
    },
    ru: {
      short: { month: '2-digit', day: '2-digit', year: 'numeric' }
    },
    hu: {
      short: { month: '2-digit', day: '2-digit', year: 'numeric' }
    }
  }
};

const i18nConfigs = {
  cz: {
    fallbackLocale: 'cs',
    locale: 'cs',
    messages: {
      cs: { ...langs.csCZLang, countries: buildCzRegionCountryMap('cs') },
      sk: { ...langs.skCZLang, countries: buildCzRegionCountryMap('sk') },
      uk: { ...langs.ukCZLang, countries: buildCzRegionCountryMap('uk') },
      ru: { ...langs.ruCZLang, countries: buildCzRegionCountryMap('ru') },
      en: { ...langs.enCZLang, countries: buildCzRegionCountryMap('en') },
      hu: { ...langs.huCZLang, countries: buildCzRegionCountryMap('hu') }
    }
  },
  sk: {
    fallbackLocale: 'sk',
    locale: 'sk',
    messages: {
      cz: { ...langs.csSKLang, countries: buildSkRegionCountryMap('cs') },
      sk: { ...langs.skSKLang, countries: buildSkRegionCountryMap('sk') },
      uk: { ...langs.ukSKLang, countries: buildSkRegionCountryMap('uk') },
      ru: { ...langs.ruSKLang, countries: buildSkRegionCountryMap('ru') },
      en: { ...langs.enSKLang, countries: buildSkRegionCountryMap('en') },
      hu: { ...langs.huSKLang, countries: buildSkRegionCountryMap('hu') }
    }
  },
  hu: {
    fallbackLocale: 'hu',
    locale: 'hu',
    messages: {
      cz: { ...langs.csHULang, countries: buildHuRegionCountryMap('cz') },
      sk: { ...langs.skHULang, countries: buildHuRegionCountryMap('sk') },
      uk: { ...langs.ukHULang, countries: buildHuRegionCountryMap('uk') },
      ru: { ...langs.ruHULang, countries: buildHuRegionCountryMap('ru') },
      en: { ...langs.enHULang, countries: buildHuRegionCountryMap('en') },
      hu: { ...langs.huHULang, countries: buildHuRegionCountryMap('hu') }
    }
  }
};

export const i18nCZ = new VueI18n({ ...i18nConfigs['cz'] });

export function createI18n(region = 'cz') {
  const config = i18nConfigs[region] || i18nConfigs.cz;
  return new VueI18n({ ...config, ...formattingBase });
}
